import type { SkillNode, Subject, AgeBand } from "./learningTypes.js";

// ══════════════════════════════════════════════════════════════
// Skill Graph — the spine of the learning engine.
// Each node defines prerequisites, what it unlocks, mastery
// criteria, common errors, and what visuals can support it.
// ══════════════════════════════════════════════════════════════

const MATH_SKILLS: SkillNode[] = [
  // ══════════════════════════════════════════════════════════════
  // PRE-ARITHMETIC (ages 4.0-4.5) — Kumon-style: count concrete objects,
  // recognize numerals, write numerals. NO arithmetic yet. Lots of repetition.
  // ══════════════════════════════════════════════════════════════
  {
    skillId: "math.count.objects.to5",
    subject: "math", name: "Count objects to 5", ageBandMin: "4.0-4.5", ageBandTypical: "4.0-4.5", ageBandStretch: "4.5-5.0",
    description: "Tap or count objects in a group of 1-5 and say how many",
    prerequisites: [],
    unlocks: ["math.count.objects.to10", "math.recognize.numerals.0_to_5"],
    masteryCriteria: { minAttempts: 8, recentAccuracyThreshold: 0.85, transferRequired: false, maxSupportLevel: 4, minLowSupportRate: 0.6 },
    commonErrors: ["off_by_one", "random_guess"],
    allowedItemTypes: ["count"],
    visualHelpers: ["concrete_objects", "dot_rows"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: null,
    transferSkillIds: ["math.count.objects.to10"],
    placementWeight: 0.5,
  },
  {
    skillId: "math.recognize.numerals.0_to_5",
    subject: "math", name: "Recognize numerals 0-5", ageBandMin: "4.0-4.5", ageBandTypical: "4.0-4.5", ageBandStretch: "4.5-5.0",
    description: "Tap the numeral 0 through 5 when shown",
    prerequisites: [],
    unlocks: ["math.write.numerals.0_to_5", "math.recognize.numerals.0_to_10"],
    masteryCriteria: { minAttempts: 6, recentAccuracyThreshold: 0.90, transferRequired: false, maxSupportLevel: 2, minLowSupportRate: 0.80 },
    commonErrors: ["random_guess"],
    allowedItemTypes: ["recognize_numeral"],
    visualHelpers: ["worked_example_panel"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: null,
    transferSkillIds: ["math.write.numerals.0_to_5"],
    placementWeight: 0.5,
  },
  {
    skillId: "math.count.objects.to10",
    subject: "math", name: "Count objects to 10", ageBandMin: "4.5-5.0", ageBandTypical: "4.5-5.0", ageBandStretch: "5.0-5.5",
    description: "Count groups of 6-10 objects accurately",
    prerequisites: ["math.count.objects.to5"],
    unlocks: ["math.recognize.numerals.0_to_10", "math.count.to20"],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.85, transferRequired: false, maxSupportLevel: 3, minLowSupportRate: 0.7 },
    commonErrors: ["off_by_one"],
    allowedItemTypes: ["count"],
    visualHelpers: ["concrete_objects", "dot_rows"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.count.objects.to5",
    transferSkillIds: ["math.recognize.numerals.0_to_10"],
    placementWeight: 0.6,
  },
  {
    skillId: "math.recognize.numerals.0_to_10",
    subject: "math", name: "Recognize numerals 0-10", ageBandMin: "4.5-5.0", ageBandTypical: "4.5-5.0", ageBandStretch: "5.0-5.5",
    description: "Tap the numeral 0 through 10 when shown",
    prerequisites: ["math.recognize.numerals.0_to_5"],
    unlocks: ["math.write.numerals.0_to_10", "math.count.to20"],
    masteryCriteria: { minAttempts: 8, recentAccuracyThreshold: 0.88, transferRequired: false, maxSupportLevel: 1, minLowSupportRate: 0.80 },
    commonErrors: ["random_guess"],
    allowedItemTypes: ["recognize_numeral"],
    visualHelpers: ["worked_example_panel"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.recognize.numerals.0_to_5",
    transferSkillIds: ["math.write.numerals.0_to_10"],
    placementWeight: 0.6,
  },
  {
    skillId: "math.write.numerals.0_to_5",
    subject: "math", name: "Write numerals 0-5", ageBandMin: "4.0-4.5", ageBandTypical: "4.5-5.0", ageBandStretch: "5.0-5.5",
    description: "Trace and form the numerals 0-5",
    prerequisites: ["math.recognize.numerals.0_to_5"],
    unlocks: ["math.write.numerals.0_to_10"],
    masteryCriteria: { minAttempts: 12, recentAccuracyThreshold: 0.80, transferRequired: false, maxSupportLevel: 4, minLowSupportRate: 0.6 },
    commonErrors: ["unknown"],
    allowedItemTypes: ["write_numeral"],
    visualHelpers: ["worked_example_panel"],
    reviewSchedule: [1, 7, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.recognize.numerals.0_to_5",
    transferSkillIds: ["math.write.numerals.0_to_10"],
    placementWeight: 0.4,
  },
  {
    skillId: "math.write.numerals.0_to_10",
    subject: "math", name: "Write numerals 0-10", ageBandMin: "4.5-5.0", ageBandTypical: "5.0-5.5", ageBandStretch: "5.5-6.0",
    description: "Trace and form numerals 6-10",
    prerequisites: ["math.write.numerals.0_to_5"],
    unlocks: ["math.count.to20"],
    masteryCriteria: { minAttempts: 14, recentAccuracyThreshold: 0.80, transferRequired: false, maxSupportLevel: 3, minLowSupportRate: 0.65 },
    commonErrors: ["unknown"],
    allowedItemTypes: ["write_numeral"],
    visualHelpers: ["worked_example_panel"],
    reviewSchedule: [1, 7, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.write.numerals.0_to_5",
    transferSkillIds: [],
    placementWeight: 0.4,
  },
  {
    skillId: "math.shapes.basic",
    subject: "math", name: "Basic shapes", ageBandMin: "4.0-4.5", ageBandTypical: "4.0-4.5", ageBandStretch: "4.5-5.0",
    description: "Identify circle, square, triangle, star, heart, diamond, rectangle",
    prerequisites: [],
    unlocks: ["math.patterns.simple"],
    masteryCriteria: { minAttempts: 7, recentAccuracyThreshold: 0.85, transferRequired: false, maxSupportLevel: 3, minLowSupportRate: 0.7 },
    commonErrors: ["shape_confusion", "random_guess"],
    allowedItemTypes: ["shape_recognize"],
    visualHelpers: ["worked_example_panel"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: null,
    transferSkillIds: ["math.patterns.simple"],
    placementWeight: 0.4,
  },
  {
    skillId: "math.patterns.simple",
    subject: "math", name: "Simple patterns", ageBandMin: "4.0-4.5", ageBandTypical: "4.5-5.0", ageBandStretch: "5.0-5.5",
    description: "Extend ABAB / AABB / ABC patterns",
    prerequisites: ["math.shapes.basic"],
    unlocks: [],
    masteryCriteria: { minAttempts: 6, recentAccuracyThreshold: 0.80, transferRequired: false, maxSupportLevel: 3, minLowSupportRate: 0.65 },
    commonErrors: ["pattern_break", "random_guess"],
    allowedItemTypes: ["pattern_complete"],
    visualHelpers: ["worked_example_panel"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.shapes.basic",
    transferSkillIds: [],
    placementWeight: 0.5,
  },
  // ══════════════════════════════════════════════════════════════
  // ARITHMETIC (ages 5+) — existing skills, prerequisite chain updated to
  // require pre-arithmetic before counting to 20.
  // ══════════════════════════════════════════════════════════════
  {
    skillId: "math.count.to20",
    subject: "math", name: "Count to 20", ageBandMin: "5.0-5.5", ageBandTypical: "5.0-5.5", ageBandStretch: "5.5-6.0",
    description: "Count objects and read/write numerals to 20",
    prerequisites: ["math.recognize.numerals.0_to_10"],
    unlocks: ["math.subitize.to5", "math.number_bonds.to5"],
    masteryCriteria: { minAttempts: 8, recentAccuracyThreshold: 0.85, transferRequired: false, maxSupportLevel: 2, minLowSupportRate: 0.7 },
    commonErrors: ["off_by_one", "count_all_instead_of_count_on"],
    allowedItemTypes: ["count", "write_numeral", "subitize"],
    visualHelpers: ["concrete_objects", "dot_rows"],
    reviewSchedule: [1, 7, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.recognize.numerals.0_to_10",
    transferSkillIds: ["math.subitize.to5"],
    placementWeight: 0.8,
  },
  {
    skillId: "math.subitize.to5",
    subject: "math", name: "Subitize to 5", ageBandMin: "5.0-5.5", ageBandTypical: "5.0-5.5", ageBandStretch: "5.5-6.0",
    description: "Instantly recognize quantities up to 5 without counting",
    prerequisites: ["math.count.to20"],
    unlocks: ["math.number_bonds.to5", "math.add.within5.visual"],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.90, transferRequired: false, maxSupportLevel: 1, minLowSupportRate: 0.80 },
    commonErrors: ["off_by_one", "random_guess"],
    allowedItemTypes: ["subitize"],
    visualHelpers: ["dot_rows", "five_frame"],
    reviewSchedule: [1, 7, 30],
    advancementRule: "fluency_threshold",
    walkbackSkillId: "math.count.to20",
    transferSkillIds: ["math.number_bonds.to5"],
    placementWeight: 0.9,
  },
  {
    skillId: "math.number_bonds.to5",
    subject: "math", name: "Number bonds to 5", ageBandMin: "5.0-5.5", ageBandTypical: "5.0-5.5", ageBandStretch: "5.5-6.0",
    description: "Know all pairs that make 5 (0+5, 1+4, 2+3, 3+2, 4+1, 5+0)",
    prerequisites: ["math.subitize.to5"],
    unlocks: ["math.add.within5.visual", "math.sub.within5.visual"],
    masteryCriteria: { minAttempts: 12, recentAccuracyThreshold: 0.88, transferRequired: true, maxSupportLevel: 2, minLowSupportRate: 0.75 },
    commonErrors: ["complement_gap", "off_by_one"],
    allowedItemTypes: ["compose", "missing_addend"],
    visualHelpers: ["five_frame", "part_part_whole"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.subitize.to5",
    transferSkillIds: ["math.add.within5.visual"],
    placementWeight: 1.0,
  },
  {
    skillId: "math.add.within5.visual",
    subject: "math", name: "Addition within 5 (visual)", ageBandMin: "5.0-5.5", ageBandTypical: "5.0-5.5", ageBandStretch: "5.5-6.0",
    description: "Add two numbers with sum ≤ 5, using visual/concrete support",
    prerequisites: ["math.number_bonds.to5"],
    unlocks: ["math.sub.within5.visual", "math.number_bonds.to10"],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.85, transferRequired: true, maxSupportLevel: 3, minLowSupportRate: 0.70 },
    commonErrors: ["count_all_instead_of_count_on", "off_by_one"],
    allowedItemTypes: ["add", "word_problem_join"],
    visualHelpers: ["five_frame", "concrete_objects", "part_part_whole"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.number_bonds.to5",
    transferSkillIds: ["math.sub.within5.visual", "math.add.within10.visual"],
    placementWeight: 1.1,
  },
  {
    skillId: "math.sub.within5.visual",
    subject: "math", name: "Subtraction within 5 (visual)", ageBandMin: "5.0-5.5", ageBandTypical: "5.0-5.5", ageBandStretch: "5.5-6.0",
    description: "Subtract within 5 using visual/concrete support",
    prerequisites: ["math.add.within5.visual"],
    unlocks: ["math.number_bonds.to10"],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.85, transferRequired: true, maxSupportLevel: 3, minLowSupportRate: 0.70 },
    commonErrors: ["operation_confusion", "count_all_instead_of_count_on"],
    allowedItemTypes: ["subtract", "word_problem_separate"],
    visualHelpers: ["five_frame", "concrete_objects"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.add.within5.visual",
    transferSkillIds: ["math.number_bonds.to10"],
    placementWeight: 1.1,
  },
  {
    skillId: "math.number_bonds.to10",
    subject: "math", name: "Number bonds to 10", ageBandMin: "5.5-6.0", ageBandTypical: "5.5-6.0", ageBandStretch: "6.0-6.5",
    description: "Know all pairs that make 10 fluently",
    prerequisites: ["math.sub.within5.visual"],
    unlocks: ["math.add.within10.visual", "math.add.within20.make10"],
    masteryCriteria: { minAttempts: 14, recentAccuracyThreshold: 0.88, transferRequired: true, maxSupportLevel: 2, minLowSupportRate: 0.80 },
    commonErrors: ["complement_gap", "off_by_one", "random_guess"],
    allowedItemTypes: ["compose", "missing_addend", "add"],
    visualHelpers: ["ten_frame", "part_part_whole"],
    reviewSchedule: [1, 3, 7, 14, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.number_bonds.to5",
    transferSkillIds: ["math.add.within10.fluency", "math.add.within20.make10"],
    placementWeight: 1.2,
  },
  {
    skillId: "math.add.within10.visual",
    subject: "math", name: "Addition within 10 (visual)", ageBandMin: "5.5-6.0", ageBandTypical: "5.5-6.0", ageBandStretch: "6.0-6.5",
    description: "Add two numbers with sum ≤ 10 using ten-frame or count-on",
    prerequisites: ["math.number_bonds.to10"],
    unlocks: ["math.add.within10.fluency", "math.sub.within10.visual"],
    masteryCriteria: { minAttempts: 12, recentAccuracyThreshold: 0.85, transferRequired: true, maxSupportLevel: 2, minLowSupportRate: 0.70 },
    commonErrors: ["count_all_instead_of_count_on", "off_by_one"],
    allowedItemTypes: ["add", "word_problem_join"],
    visualHelpers: ["ten_frame", "number_line", "concrete_objects"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.number_bonds.to10",
    transferSkillIds: ["math.add.within10.fluency"],
    placementWeight: 1.2,
  },
  {
    skillId: "math.add.within10.fluency",
    subject: "math", name: "Addition within 10 (fluent)", ageBandMin: "6.0-6.5", ageBandTypical: "6.0-6.5", ageBandStretch: "6.5-7.0",
    description: "Retrieve addition facts within 10 without counting",
    prerequisites: ["math.add.within10.visual"],
    unlocks: ["math.add.within20.count_on", "math.add.within20.doubles"],
    masteryCriteria: { minAttempts: 16, recentAccuracyThreshold: 0.90, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.85 },
    commonErrors: ["count_all_instead_of_count_on", "random_guess"],
    allowedItemTypes: ["add"],
    visualHelpers: ["ten_frame", "number_line"],
    reviewSchedule: [1, 3, 7, 14, 30],
    advancementRule: "fluency_threshold",
    walkbackSkillId: "math.add.within10.visual",
    transferSkillIds: ["math.sub.within10.count_back"],
    placementWeight: 1.3,
  },
  {
    skillId: "math.sub.within10.visual",
    subject: "math", name: "Subtraction within 10 (visual)", ageBandMin: "5.5-6.0", ageBandTypical: "6.0-6.5", ageBandStretch: "6.5-7.0",
    description: "Subtract within 10 with visual support",
    prerequisites: ["math.add.within10.visual"],
    unlocks: ["math.sub.within10.count_back"],
    masteryCriteria: { minAttempts: 12, recentAccuracyThreshold: 0.85, transferRequired: true, maxSupportLevel: 2, minLowSupportRate: 0.70 },
    commonErrors: ["operation_confusion", "bridge_ten_gap"],
    allowedItemTypes: ["subtract", "word_problem_separate"],
    visualHelpers: ["ten_frame", "number_line"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.add.within10.visual",
    transferSkillIds: ["math.sub.within10.count_back"],
    placementWeight: 1.2,
  },
  {
    skillId: "math.sub.within10.count_back",
    subject: "math", name: "Subtraction within 10 (count back)", ageBandMin: "6.0-6.5", ageBandTypical: "6.0-6.5", ageBandStretch: "6.5-7.0",
    description: "Subtract within 10 using count-back strategy",
    prerequisites: ["math.sub.within10.visual"],
    unlocks: ["math.sub.within20.bridge10", "math.fact_family.within20"],
    masteryCriteria: { minAttempts: 14, recentAccuracyThreshold: 0.87, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.78 },
    commonErrors: ["count_all_instead_of_count_on", "off_by_one"],
    allowedItemTypes: ["subtract"],
    visualHelpers: ["number_line"],
    reviewSchedule: [1, 3, 7, 14, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.sub.within10.visual",
    transferSkillIds: ["math.sub.within20.bridge10"],
    placementWeight: 1.3,
  },
  {
    skillId: "math.add.within20.count_on",
    subject: "math", name: "Addition within 20 (count on)", ageBandMin: "6.0-6.5", ageBandTypical: "6.0-6.5", ageBandStretch: "6.5-7.0",
    description: "Add within 20 using count-on from larger strategy",
    prerequisites: ["math.add.within10.fluency"],
    unlocks: ["math.add.within20.doubles"],
    masteryCriteria: { minAttempts: 12, recentAccuracyThreshold: 0.85, transferRequired: true, maxSupportLevel: 2, minLowSupportRate: 0.72 },
    commonErrors: ["count_all_instead_of_count_on", "off_by_one"],
    allowedItemTypes: ["add"],
    visualHelpers: ["number_line"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.add.within10.fluency",
    transferSkillIds: ["math.add.within20.doubles"],
    placementWeight: 1.3,
  },
  {
    skillId: "math.add.within20.doubles",
    subject: "math", name: "Doubles to 20", ageBandMin: "6.0-6.5", ageBandTypical: "6.0-6.5", ageBandStretch: "6.5-7.0",
    description: "Know all doubles facts: 1+1 through 9+9",
    prerequisites: ["math.add.within20.count_on"],
    unlocks: ["math.add.within20.near_doubles"],
    masteryCriteria: { minAttempts: 12, recentAccuracyThreshold: 0.90, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.85 },
    commonErrors: ["double_confusion", "off_by_one"],
    allowedItemTypes: ["add"],
    visualHelpers: ["dot_rows", "double_plus_one"],
    reviewSchedule: [1, 3, 7, 14, 30],
    advancementRule: "fluency_threshold",
    walkbackSkillId: "math.add.within20.count_on",
    transferSkillIds: ["math.add.within20.near_doubles"],
    placementWeight: 1.4,
  },
  {
    skillId: "math.add.within20.near_doubles",
    subject: "math", name: "Near doubles", ageBandMin: "6.0-6.5", ageBandTypical: "6.5-7.0", ageBandStretch: "7.0-7.5",
    description: "Use doubles to find near-doubles (6+7 = 6+6+1 = 13)",
    prerequisites: ["math.add.within20.doubles"],
    unlocks: ["math.add.within20.make10"],
    masteryCriteria: { minAttempts: 12, recentAccuracyThreshold: 0.87, transferRequired: true, maxSupportLevel: 2, minLowSupportRate: 0.75 },
    commonErrors: ["near_double_adjustment_gap", "double_confusion"],
    allowedItemTypes: ["add"],
    visualHelpers: ["double_plus_one", "dot_rows"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.add.within20.doubles",
    transferSkillIds: ["math.add.within20.make10"],
    placementWeight: 1.4,
  },
  {
    skillId: "math.add.within20.make10",
    subject: "math", name: "Make 10 to add", ageBandMin: "6.0-6.5", ageBandTypical: "6.5-7.0", ageBandStretch: "7.0-7.5",
    description: "Decompose addend to make 10, then add remainder (8+5 = 10+3 = 13)",
    prerequisites: ["math.number_bonds.to10", "math.add.within20.near_doubles"],
    unlocks: ["math.sub.within20.bridge10", "math.missing_addend.within20"],
    masteryCriteria: { minAttempts: 14, recentAccuracyThreshold: 0.87, transferRequired: true, maxSupportLevel: 2, minLowSupportRate: 0.78 },
    commonErrors: ["make_ten_leftover_gap", "complement_gap"],
    allowedItemTypes: ["add", "word_problem_join"],
    visualHelpers: ["ten_frame", "make_ten_bridge"],
    reviewSchedule: [1, 3, 7, 14, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.number_bonds.to10",
    transferSkillIds: ["math.sub.within20.bridge10"],
    placementWeight: 1.5,
  },
  {
    skillId: "math.sub.within20.bridge10",
    subject: "math", name: "Bridge-10 subtraction", ageBandMin: "6.5-7.0", ageBandTypical: "6.5-7.0", ageBandStretch: "7.0-7.5",
    description: "Subtract by bridging through 10 (13-5 → 13-3=10, 10-2=8)",
    prerequisites: ["math.add.within20.make10", "math.sub.within10.count_back"],
    unlocks: ["math.fact_family.within20"],
    masteryCriteria: { minAttempts: 14, recentAccuracyThreshold: 0.85, transferRequired: true, maxSupportLevel: 2, minLowSupportRate: 0.75 },
    commonErrors: ["bridge_ten_gap", "make_ten_leftover_gap"],
    allowedItemTypes: ["subtract", "word_problem_separate"],
    visualHelpers: ["bridge_ten_subtraction", "number_line", "ten_frame"],
    reviewSchedule: [1, 3, 7, 14, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.sub.within10.count_back",
    transferSkillIds: ["math.fact_family.within20"],
    placementWeight: 1.5,
  },
  {
    skillId: "math.fact_family.within20",
    subject: "math", name: "Fact families within 20", ageBandMin: "6.5-7.0", ageBandTypical: "7.0-7.5", ageBandStretch: "7.0-7.5",
    description: "Use fact family relationships to derive related facts",
    prerequisites: ["math.sub.within20.bridge10"],
    unlocks: ["math.missing_addend.within20"],
    masteryCriteria: { minAttempts: 12, recentAccuracyThreshold: 0.87, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.80 },
    commonErrors: ["operation_confusion", "missing_addend_confusion"],
    allowedItemTypes: ["add", "subtract", "missing_addend"],
    visualHelpers: ["fact_family_triangle", "part_part_whole"],
    reviewSchedule: [1, 7, 14, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.sub.within20.bridge10",
    transferSkillIds: ["math.missing_addend.within20"],
    placementWeight: 1.5,
  },
  {
    skillId: "math.missing_addend.within10",
    subject: "math", name: "Missing addend within 10", ageBandMin: "5.5-6.0", ageBandTypical: "6.0-6.5", ageBandStretch: "6.5-7.0",
    description: "Find the unknown part: 3 + ___ = 8",
    prerequisites: ["math.number_bonds.to10"],
    unlocks: ["math.missing_addend.within20"],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.85, transferRequired: true, maxSupportLevel: 2, minLowSupportRate: 0.72 },
    commonErrors: ["missing_addend_confusion", "operation_confusion"],
    allowedItemTypes: ["missing_addend"],
    visualHelpers: ["part_part_whole", "ten_frame"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.number_bonds.to10",
    transferSkillIds: ["math.missing_addend.within20"],
    placementWeight: 1.3,
  },
  {
    skillId: "math.missing_addend.within20",
    subject: "math", name: "Missing addend within 20", ageBandMin: "6.5-7.0", ageBandTypical: "7.0-7.5", ageBandStretch: "7.0-7.5",
    description: "Find the unknown: 8 + ___ = 17",
    prerequisites: ["math.missing_addend.within10", "math.add.within20.make10"],
    unlocks: ["math.word.unknowns"],
    masteryCriteria: { minAttempts: 12, recentAccuracyThreshold: 0.85, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.78 },
    commonErrors: ["missing_addend_confusion", "bridge_ten_gap"],
    allowedItemTypes: ["missing_addend"],
    visualHelpers: ["part_part_whole", "number_line"],
    reviewSchedule: [1, 3, 7, 14, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.missing_addend.within10",
    transferSkillIds: ["math.word.unknowns"],
    placementWeight: 1.5,
  },
  {
    skillId: "math.place_value.teens",
    subject: "math", name: "Teen numbers", ageBandMin: "6.0-6.5", ageBandTypical: "6.0-6.5", ageBandStretch: "6.5-7.0",
    description: "Understand teen numbers as 10 + ones (14 = 10 + 4)",
    prerequisites: ["math.number_bonds.to10"],
    unlocks: ["math.place_value.tens_ones_100"],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.87, transferRequired: true, maxSupportLevel: 2, minLowSupportRate: 0.75 },
    commonErrors: ["place_value_reversal"],
    allowedItemTypes: ["place_value"],
    visualHelpers: ["ten_frame", "base_ten_blocks"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.number_bonds.to10",
    transferSkillIds: ["math.place_value.tens_ones_100"],
    placementWeight: 1.3,
  },
  {
    skillId: "math.place_value.tens_ones_100",
    subject: "math", name: "Tens and ones to 100", ageBandMin: "6.5-7.0", ageBandTypical: "7.0-7.5", ageBandStretch: "7.0-7.5",
    description: "Understand 2-digit numbers as tens + ones (47 = 4 tens + 7 ones)",
    prerequisites: ["math.place_value.teens"],
    unlocks: ["math.add.2digit.no_regroup"],
    masteryCriteria: { minAttempts: 12, recentAccuracyThreshold: 0.87, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.78 },
    commonErrors: ["place_value_reversal"],
    allowedItemTypes: ["place_value"],
    visualHelpers: ["base_ten_blocks"],
    reviewSchedule: [1, 3, 7, 14, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.place_value.teens",
    transferSkillIds: ["math.add.2digit.no_regroup"],
    placementWeight: 1.5,
  },
  {
    skillId: "math.add.2digit.no_regroup",
    subject: "math", name: "2-digit addition (no regrouping)", ageBandMin: "7.0-7.5", ageBandTypical: "7.0-7.5", ageBandStretch: "7.0-7.5",
    description: "Add 2-digit numbers without regrouping (23 + 14 = 37)",
    prerequisites: ["math.place_value.tens_ones_100", "math.add.within10.fluency"],
    unlocks: [],
    masteryCriteria: { minAttempts: 12, recentAccuracyThreshold: 0.87, transferRequired: true, maxSupportLevel: 2, minLowSupportRate: 0.75 },
    commonErrors: ["place_value_reversal", "off_by_one"],
    allowedItemTypes: ["two_digit_add"],
    visualHelpers: ["base_ten_blocks"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.place_value.tens_ones_100",
    transferSkillIds: [],
    placementWeight: 1.5,
  },
  {
    skillId: "math.word.add_take_apart",
    subject: "math", name: "Word problems (join/separate)", ageBandMin: "6.0-6.5", ageBandTypical: "6.0-6.5", ageBandStretch: "6.5-7.0",
    description: "Solve join and separate word problems within working range",
    prerequisites: ["math.add.within10.fluency", "math.sub.within10.count_back"],
    unlocks: ["math.word.unknowns"],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.83, transferRequired: true, maxSupportLevel: 2, minLowSupportRate: 0.70 },
    commonErrors: ["operation_confusion", "random_guess"],
    allowedItemTypes: ["word_problem_join", "word_problem_separate", "word_problem_compare", "word_problem_part_whole"],
    visualHelpers: ["number_line", "part_part_whole", "concrete_objects"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.add.within10.fluency",
    transferSkillIds: ["math.word.unknowns"],
    placementWeight: 1.4,
  },
  {
    skillId: "math.word.unknowns",
    subject: "math", name: "Word problems with unknowns", ageBandMin: "7.0-7.5", ageBandTypical: "7.0-7.5", ageBandStretch: "7.0-7.5",
    description: "Solve word problems where the unknown is start, change, or result",
    prerequisites: ["math.word.add_take_apart", "math.missing_addend.within20"],
    unlocks: [],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.83, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.72 },
    commonErrors: ["operation_confusion", "missing_addend_confusion"],
    allowedItemTypes: ["word_problem_join", "word_problem_separate"],
    visualHelpers: ["number_line", "part_part_whole"],
    reviewSchedule: [1, 7, 14, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "math.word.add_take_apart",
    transferSkillIds: [],
    placementWeight: 1.5,
  },
];

const READING_SKILLS: SkillNode[] = [
  {
    skillId: "reading.phonological.rhyme",
    subject: "reading", name: "Rhyming", ageBandMin: "4.0-4.5", ageBandTypical: "4.0-4.5", ageBandStretch: "4.5-5.0",
    description: "Identify words that rhyme with a target word",
    prerequisites: [],
    unlocks: ["reading.phonological.syllable_count", "reading.letter.lowercase.recognition"],
    masteryCriteria: { minAttempts: 8, recentAccuracyThreshold: 0.82, transferRequired: false, maxSupportLevel: 3, minLowSupportRate: 0.7 },
    commonErrors: ["rhyme_mismatch", "random_guess"],
    allowedItemTypes: ["rhyme"],
    visualHelpers: ["word_picture"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: null,
    transferSkillIds: ["reading.phonological.syllable_count"],
    placementWeight: 0.6,
  },
  {
    skillId: "reading.phonological.syllable_count",
    subject: "reading", name: "Syllable counting", ageBandMin: "4.0-4.5", ageBandTypical: "4.5-5.0", ageBandStretch: "5.0-5.5",
    description: "Count syllables in spoken words (1-4 syllables)",
    prerequisites: ["reading.phonological.rhyme"],
    unlocks: ["reading.letter.lowercase.recognition"],
    masteryCriteria: { minAttempts: 8, recentAccuracyThreshold: 0.80, transferRequired: false, maxSupportLevel: 3, minLowSupportRate: 0.65 },
    commonErrors: ["syllable_count_off", "random_guess"],
    allowedItemTypes: ["syllable_count"],
    visualHelpers: ["word_picture"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "reading.phonological.rhyme",
    transferSkillIds: ["reading.letter.lowercase.recognition"],
    placementWeight: 0.7,
  },
  {
    skillId: "reading.letter.lowercase.recognition",
    subject: "reading", name: "Lowercase letter recognition", ageBandMin: "5.0-5.5", ageBandTypical: "5.0-5.5", ageBandStretch: "5.5-6.0",
    description: "Identify all 26 lowercase letters by name",
    prerequisites: [],
    unlocks: ["reading.sound.initial", "reading.phonemic.blend.cvc_oral"],
    masteryCriteria: { minAttempts: 26, recentAccuracyThreshold: 0.88, transferRequired: false, maxSupportLevel: 2, minLowSupportRate: 0.80 },
    commonErrors: ["initial_sound_guess"],
    allowedItemTypes: ["letter_name"],
    visualHelpers: ["word_picture"],
    reviewSchedule: [1, 7, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: null,
    transferSkillIds: ["reading.sound.initial"],
    placementWeight: 0.8,
  },
  {
    skillId: "reading.sound.initial",
    subject: "reading", name: "Initial sounds", ageBandMin: "5.0-5.5", ageBandTypical: "5.0-5.5", ageBandStretch: "5.5-6.0",
    description: "Identify the first sound in spoken words",
    prerequisites: ["reading.letter.lowercase.recognition"],
    unlocks: ["reading.phonemic.blend.cvc_oral"],
    masteryCriteria: { minAttempts: 12, recentAccuracyThreshold: 0.87, transferRequired: false, maxSupportLevel: 2, minLowSupportRate: 0.78 },
    commonErrors: ["initial_sound_guess", "vowel_substitution"],
    allowedItemTypes: ["letter_sound"],
    visualHelpers: ["word_picture"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "reading.letter.lowercase.recognition",
    transferSkillIds: ["reading.phonemic.blend.cvc_oral"],
    placementWeight: 1.0,
  },
  {
    skillId: "reading.phonemic.blend.cvc_oral",
    subject: "reading", name: "Oral blending (CVC)", ageBandMin: "5.0-5.5", ageBandTypical: "5.0-5.5", ageBandStretch: "5.5-6.0",
    description: "Blend spoken phonemes into a CVC word",
    prerequisites: ["reading.sound.initial"],
    unlocks: ["reading.cvc.short_a.decode", "reading.cvc.encode"],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.87, transferRequired: false, maxSupportLevel: 2, minLowSupportRate: 0.75 },
    commonErrors: ["blend_drop", "vowel_substitution"],
    allowedItemTypes: ["blend"],
    visualHelpers: ["word_picture"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "reading.sound.initial",
    transferSkillIds: ["reading.cvc.short_a.decode"],
    placementWeight: 1.1,
  },
  {
    skillId: "reading.cvc.short_a.decode",
    subject: "reading", name: "CVC short-a decoding", ageBandMin: "5.0-5.5", ageBandTypical: "5.5-6.0", ageBandStretch: "6.0-6.5",
    description: "Decode printed CVC words with short /a/ (cat, map, fan)",
    prerequisites: ["reading.phonemic.blend.cvc_oral"],
    unlocks: ["reading.cvc.all_short_vowels.decode"],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.87, transferRequired: false, maxSupportLevel: 2, minLowSupportRate: 0.75 },
    commonErrors: ["vowel_substitution", "final_sound_ignored"],
    allowedItemTypes: ["cvc_decode"],
    visualHelpers: ["word_picture"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "reading.phonemic.blend.cvc_oral",
    transferSkillIds: ["reading.cvc.all_short_vowels.decode"],
    placementWeight: 1.2,
  },
  {
    skillId: "reading.cvc.all_short_vowels.decode",
    subject: "reading", name: "CVC all short vowels (decode)", ageBandMin: "5.5-6.0", ageBandTypical: "6.0-6.5", ageBandStretch: "6.5-7.0",
    description: "Decode CVC words across all 5 short vowels",
    prerequisites: ["reading.cvc.short_a.decode"],
    unlocks: ["reading.cvc.encode", "reading.digraphs.sh_ch_th"],
    masteryCriteria: { minAttempts: 16, recentAccuracyThreshold: 0.87, transferRequired: true, maxSupportLevel: 2, minLowSupportRate: 0.78 },
    commonErrors: ["vowel_substitution", "initial_sound_guess"],
    allowedItemTypes: ["cvc_decode"],
    visualHelpers: ["word_picture"],
    reviewSchedule: [1, 3, 7, 14, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "reading.cvc.short_a.decode",
    transferSkillIds: ["reading.cvc.encode"],
    placementWeight: 1.3,
  },
  {
    skillId: "reading.cvc.encode",
    subject: "reading", name: "CVC encoding (spelling)", ageBandMin: "5.5-6.0", ageBandTypical: "6.0-6.5", ageBandStretch: "6.5-7.0",
    description: "Spell CVC words by segmenting and writing phonemes",
    prerequisites: ["reading.cvc.all_short_vowels.decode"],
    unlocks: ["reading.blends.ccvc_cvcc"],
    masteryCriteria: { minAttempts: 14, recentAccuracyThreshold: 0.85, transferRequired: true, maxSupportLevel: 2, minLowSupportRate: 0.75 },
    commonErrors: ["vowel_substitution", "blend_drop"],
    allowedItemTypes: ["cvc_encode", "segment"],
    visualHelpers: [],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "reading.cvc.all_short_vowels.decode",
    transferSkillIds: ["reading.blends.ccvc_cvcc"],
    placementWeight: 1.3,
  },
  {
    skillId: "reading.digraphs.sh_ch_th",
    subject: "reading", name: "Digraphs sh/ch/th/ck", ageBandMin: "6.0-6.5", ageBandTypical: "6.0-6.5", ageBandStretch: "6.5-7.0",
    description: "Read and spell words with sh, ch, th, ck",
    prerequisites: ["reading.cvc.all_short_vowels.decode"],
    unlocks: ["reading.blends.ccvc_cvcc"],
    masteryCriteria: { minAttempts: 12, recentAccuracyThreshold: 0.85, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.75 },
    commonErrors: ["digraph_not_recognized", "blend_drop"],
    allowedItemTypes: ["digraph"],
    visualHelpers: ["word_picture"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "reading.cvc.all_short_vowels.decode",
    transferSkillIds: ["reading.blends.ccvc_cvcc"],
    placementWeight: 1.4,
  },
  {
    skillId: "reading.blends.ccvc_cvcc",
    subject: "reading", name: "Consonant blends CCVC/CVCC", ageBandMin: "6.0-6.5", ageBandTypical: "6.5-7.0", ageBandStretch: "7.0-7.5",
    description: "Read and spell words with initial and final blends",
    prerequisites: ["reading.digraphs.sh_ch_th", "reading.cvc.encode"],
    unlocks: ["reading.silent_e.cvc_e"],
    masteryCriteria: { minAttempts: 14, recentAccuracyThreshold: 0.85, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.75 },
    commonErrors: ["blend_drop", "vowel_substitution"],
    allowedItemTypes: ["blend_word"],
    visualHelpers: ["word_picture"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "reading.digraphs.sh_ch_th",
    transferSkillIds: ["reading.silent_e.cvc_e"],
    placementWeight: 1.4,
  },
  {
    skillId: "reading.silent_e.cvc_e",
    subject: "reading", name: "Silent-e (CVCe)", ageBandMin: "6.5-7.0", ageBandTypical: "7.0-7.5", ageBandStretch: "7.0-7.5",
    description: "Decode and distinguish CVCe long-vowel words",
    prerequisites: ["reading.blends.ccvc_cvcc"],
    unlocks: ["reading.vowel_teams.ai_ee_oa", "reading.connected.decodable.sentences"],
    masteryCriteria: { minAttempts: 12, recentAccuracyThreshold: 0.85, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.75 },
    commonErrors: ["silent_e_ignored", "vowel_substitution"],
    allowedItemTypes: ["silent_e"],
    visualHelpers: ["word_picture"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "reading.blends.ccvc_cvcc",
    transferSkillIds: ["reading.connected.decodable.sentences"],
    placementWeight: 1.5,
  },
  {
    skillId: "reading.sight.highfreq.k1",
    subject: "reading", name: "High-frequency words (K-1)", ageBandMin: "6.0-6.5", ageBandTypical: "6.0-6.5", ageBandStretch: "6.5-7.0",
    description: "Read and write common HFW (the, was, said, they, have...)",
    prerequisites: ["reading.cvc.all_short_vowels.decode"],
    unlocks: ["reading.connected.decodable.sentences"],
    masteryCriteria: { minAttempts: 16, recentAccuracyThreshold: 0.88, transferRequired: false, maxSupportLevel: 2, minLowSupportRate: 0.80 },
    commonErrors: ["sight_word_guess", "decodability_gap"],
    allowedItemTypes: ["hfw"],
    visualHelpers: [],
    reviewSchedule: [1, 3, 7, 14, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "reading.cvc.all_short_vowels.decode",
    transferSkillIds: ["reading.connected.decodable.sentences"],
    placementWeight: 1.3,
  },
  {
    skillId: "reading.connected.decodable.sentences",
    subject: "reading", name: "Decodable sentence reading", ageBandMin: "6.0-6.5", ageBandTypical: "6.5-7.0", ageBandStretch: "7.0-7.5",
    description: "Read short decodable sentences fluently",
    prerequisites: ["reading.sight.highfreq.k1", "reading.cvc.all_short_vowels.decode"],
    unlocks: ["reading.comprehension.literal"],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.87, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.78 },
    commonErrors: ["sight_word_guess", "picture_guessing"],
    allowedItemTypes: ["sentence_complete", "passage_complete"],
    visualHelpers: ["word_picture"],
    reviewSchedule: [1, 7, 14, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "reading.sight.highfreq.k1",
    transferSkillIds: ["reading.comprehension.literal"],
    placementWeight: 1.4,
  },
  {
    skillId: "reading.comprehension.literal",
    subject: "reading", name: "Literal comprehension", ageBandMin: "7.0-7.5", ageBandTypical: "7.0-7.5", ageBandStretch: "7.0-7.5",
    description: "Answer literal questions about short decodable passages",
    prerequisites: ["reading.connected.decodable.sentences"],
    unlocks: [],
    masteryCriteria: { minAttempts: 8, recentAccuracyThreshold: 0.83, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.75 },
    commonErrors: ["picture_guessing", "sight_word_guess"],
    allowedItemTypes: ["passage_complete"],
    visualHelpers: [],
    reviewSchedule: [1, 7, 14, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "reading.connected.decodable.sentences",
    transferSkillIds: [],
    placementWeight: 1.5,
  },
  {
    skillId: "reading.vowel_teams.ai_ee_oa",
    subject: "reading", name: "Vowel teams (ai, ee, oa)", ageBandMin: "7.0-7.5", ageBandTypical: "7.0-7.5", ageBandStretch: "7.0-7.5",
    description: "Decode words with common vowel teams",
    prerequisites: ["reading.silent_e.cvc_e"],
    unlocks: [],
    masteryCriteria: { minAttempts: 12, recentAccuracyThreshold: 0.85, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.75 },
    commonErrors: ["vowel_substitution", "decodability_gap"],
    allowedItemTypes: ["cvc_decode"],
    visualHelpers: ["word_picture"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "reading.silent_e.cvc_e",
    transferSkillIds: [],
    placementWeight: 1.5,
  },
  {
    skillId: "reading.silent_letters",
    subject: "reading", name: "Silent letters", ageBandMin: "6.5-7.0", ageBandTypical: "7.0-7.5", ageBandStretch: "7.0-7.5",
    description: "Recognize words where one letter is silent (kn, mb, wr, gh)",
    prerequisites: ["reading.blends.ccvc_cvcc"],
    unlocks: [],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.85, transferRequired: false, maxSupportLevel: 2, minLowSupportRate: 0.7 },
    commonErrors: ["decodability_gap"],
    allowedItemTypes: ["silent_letter"],
    visualHelpers: ["word_picture"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "reading.blends.ccvc_cvcc",
    transferSkillIds: [],
    placementWeight: 1.4,
  },
  {
    skillId: "reading.vocabulary.academic_tier2",
    subject: "reading", name: "Academic vocabulary (Tier 2)", ageBandMin: "6.0-6.5", ageBandTypical: "6.5-7.0", ageBandStretch: "7.0-7.5",
    description: "Build domain-general school vocabulary (describe, compare, explain, observe, predict, similar, different, before, after, etc.)",
    prerequisites: ["reading.connected.decodable.sentences"],
    unlocks: [],
    masteryCriteria: { minAttempts: 12, recentAccuracyThreshold: 0.85, transferRequired: false, maxSupportLevel: 1, minLowSupportRate: 0.78 },
    commonErrors: ["vocab_definition_mismatch", "random_guess"],
    allowedItemTypes: ["academic_vocab"],
    visualHelpers: ["sentence_builder"],
    reviewSchedule: [1, 7, 14, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "reading.connected.decodable.sentences",
    transferSkillIds: [],
    placementWeight: 1.4,
  },
  {
    skillId: "reading.doubled_consonants",
    subject: "reading", name: "Doubled consonants", ageBandMin: "6.0-6.5", ageBandTypical: "6.5-7.0", ageBandStretch: "7.0-7.5",
    description: "Read and spell words with doubled consonants (ll, tt, dd, ss, ff, nn, pp)",
    prerequisites: ["reading.cvc.encode"],
    unlocks: [],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.85, transferRequired: false, maxSupportLevel: 2, minLowSupportRate: 0.7 },
    commonErrors: ["decodability_gap"],
    allowedItemTypes: ["doubled_consonant"],
    visualHelpers: ["word_picture"],
    reviewSchedule: [1, 3, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "reading.cvc.encode",
    transferSkillIds: [],
    placementWeight: 1.3,
  },
];

const WRITING_SKILLS: SkillNode[] = [
  {
    skillId: "writing.letter.formation.lowercase",
    subject: "writing", name: "Lowercase letter formation", ageBandMin: "5.0-5.5", ageBandTypical: "5.0-5.5", ageBandStretch: "5.5-6.0",
    description: "Form all lowercase letters correctly with proper stroke order",
    prerequisites: [],
    unlocks: ["writing.word.copy.cvc", "writing.sentence.cap_period"],
    masteryCriteria: { minAttempts: 26, recentAccuracyThreshold: 0.85, transferRequired: false, maxSupportLevel: 3, minLowSupportRate: 0.70 },
    commonErrors: ["word_order_error"],
    allowedItemTypes: ["letter_form"],
    visualHelpers: ["worked_example_panel"],
    reviewSchedule: [1, 7, 30],
    advancementRule: "mastery_threshold",
    walkbackSkillId: null,
    transferSkillIds: ["writing.word.copy.cvc"],
    placementWeight: 0.8,
  },
  {
    skillId: "writing.word.copy.cvc",
    subject: "writing", name: "Copy CVC words", ageBandMin: "5.0-5.5", ageBandTypical: "5.0-5.5", ageBandStretch: "5.5-6.0",
    description: "Copy short CVC words accurately",
    prerequisites: ["writing.letter.formation.lowercase"],
    unlocks: ["writing.sentence.cap_period"],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.87, transferRequired: false, maxSupportLevel: 2, minLowSupportRate: 0.78 },
    commonErrors: ["word_order_error"],
    allowedItemTypes: ["cvc_encode"],
    visualHelpers: [],
    reviewSchedule: [1, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "writing.letter.formation.lowercase",
    transferSkillIds: ["writing.sentence.cap_period"],
    placementWeight: 1.0,
  },
  {
    skillId: "writing.sentence.cap_period",
    subject: "writing", name: "Capitalization and period", ageBandMin: "5.5-6.0", ageBandTypical: "6.0-6.5", ageBandStretch: "6.5-7.0",
    description: "Start sentences with capital letters and end with periods",
    prerequisites: ["writing.word.copy.cvc"],
    unlocks: ["writing.grammar.noun_verb", "writing.sentence.complete_simple"],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.88, transferRequired: false, maxSupportLevel: 1, minLowSupportRate: 0.82 },
    commonErrors: ["capitalization_missing", "punctuation_missing"],
    allowedItemTypes: ["sentence_complete"],
    visualHelpers: ["sentence_builder"],
    reviewSchedule: [1, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "writing.word.copy.cvc",
    transferSkillIds: ["writing.grammar.noun_verb"],
    placementWeight: 1.1,
  },
  {
    skillId: "writing.grammar.noun_verb",
    subject: "writing", name: "Nouns and verbs", ageBandMin: "5.5-6.0", ageBandTypical: "6.0-6.5", ageBandStretch: "6.5-7.0",
    description: "Identify and use nouns and verbs in simple sentences",
    prerequisites: ["writing.sentence.cap_period"],
    unlocks: ["writing.grammar.adjective.basic"],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.85, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.78 },
    commonErrors: ["noun_verb_confusion"],
    allowedItemTypes: ["sentence_complete"],
    visualHelpers: ["grammar_scene"],
    reviewSchedule: [1, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "writing.sentence.cap_period",
    transferSkillIds: ["writing.grammar.adjective.basic"],
    placementWeight: 1.2,
  },
  {
    skillId: "writing.grammar.adjective.basic",
    subject: "writing", name: "Basic adjectives", ageBandMin: "6.0-6.5", ageBandTypical: "6.0-6.5", ageBandStretch: "6.5-7.0",
    description: "Identify and use adjectives to describe nouns",
    prerequisites: ["writing.grammar.noun_verb"],
    unlocks: ["writing.adjectives.comparative_superlative", "writing.sentence.expand_adjective"],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.85, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.78 },
    commonErrors: ["adjective_confusion", "noun_verb_confusion"],
    allowedItemTypes: ["adjective_form", "sentence_complete"],
    visualHelpers: ["grammar_scene"],
    reviewSchedule: [1, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "writing.grammar.noun_verb",
    transferSkillIds: ["writing.adjectives.comparative_superlative"],
    placementWeight: 1.3,
  },
  {
    skillId: "writing.sentence.complete_simple",
    subject: "writing", name: "Complete simple sentences", ageBandMin: "6.0-6.5", ageBandTypical: "6.0-6.5", ageBandStretch: "6.5-7.0",
    description: "Write grammatically complete simple sentences",
    prerequisites: ["writing.sentence.cap_period", "writing.grammar.noun_verb"],
    unlocks: ["writing.sentence.expand_adjective"],
    masteryCriteria: { minAttempts: 8, recentAccuracyThreshold: 0.85, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.78 },
    commonErrors: ["sentence_fragment", "capitalization_missing"],
    allowedItemTypes: ["sentence_complete"],
    visualHelpers: ["sentence_builder", "grammar_scene"],
    reviewSchedule: [1, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "writing.grammar.noun_verb",
    transferSkillIds: ["writing.sentence.expand_adjective"],
    placementWeight: 1.3,
  },
  {
    skillId: "writing.sentence.expand_adjective",
    subject: "writing", name: "Expand with adjective", ageBandMin: "6.0-6.5", ageBandTypical: "6.5-7.0", ageBandStretch: "7.0-7.5",
    description: "Add an adjective to expand a simple sentence",
    prerequisites: ["writing.sentence.complete_simple", "writing.grammar.adjective.basic"],
    unlocks: ["writing.adjectives.comparative_superlative"],
    masteryCriteria: { minAttempts: 8, recentAccuracyThreshold: 0.83, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.75 },
    commonErrors: ["adjective_confusion", "word_order_error"],
    allowedItemTypes: ["adjective_form", "sentence_complete"],
    visualHelpers: ["grammar_scene"],
    reviewSchedule: [1, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "writing.grammar.adjective.basic",
    transferSkillIds: ["writing.adjectives.comparative_superlative"],
    placementWeight: 1.4,
  },
  {
    skillId: "writing.adjectives.comparative_superlative",
    subject: "writing", name: "Comparative and superlative", ageBandMin: "6.5-7.0", ageBandTypical: "7.0-7.5", ageBandStretch: "7.0-7.5",
    description: "Use -er (comparative) and -est (superlative) correctly in context",
    prerequisites: ["writing.sentence.expand_adjective"],
    unlocks: ["writing.response.2sentences"],
    masteryCriteria: { minAttempts: 10, recentAccuracyThreshold: 0.85, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.78 },
    commonErrors: ["comparative_context_gap", "superlative_context_gap"],
    allowedItemTypes: ["adjective_form"],
    visualHelpers: ["comparison_ladder", "grammar_scene"],
    reviewSchedule: [1, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "writing.sentence.expand_adjective",
    transferSkillIds: ["writing.response.2sentences"],
    placementWeight: 1.5,
  },
  {
    skillId: "writing.response.2sentences",
    subject: "writing", name: "Two-sentence response", ageBandMin: "7.0-7.5", ageBandTypical: "7.0-7.5", ageBandStretch: "7.0-7.5",
    description: "Write a two-sentence response with correct grammar and punctuation",
    prerequisites: ["writing.adjectives.comparative_superlative"],
    unlocks: [],
    masteryCriteria: { minAttempts: 6, recentAccuracyThreshold: 0.83, transferRequired: true, maxSupportLevel: 1, minLowSupportRate: 0.75 },
    commonErrors: ["sentence_fragment", "capitalization_missing", "punctuation_missing"],
    allowedItemTypes: ["sentence_complete"],
    visualHelpers: ["sentence_builder"],
    reviewSchedule: [1, 7, 14],
    advancementRule: "mastery_threshold",
    walkbackSkillId: "writing.adjectives.comparative_superlative",
    transferSkillIds: [],
    placementWeight: 1.5,
  },
];

const ALL_SKILLS: SkillNode[] = [...MATH_SKILLS, ...READING_SKILLS, ...WRITING_SKILLS];

const SKILL_MAP = new Map<string, SkillNode>(ALL_SKILLS.map((s) => [s.skillId, s]));

export function getSkill(skillId: string): SkillNode | undefined {
  return SKILL_MAP.get(skillId);
}

// MA-79 (ADR-106): keyed on canonical range labels; each former point key is
// the range starting there, and every band from 7.0 up keeps the former "7.0"
// entry.
export function getDefaultStartingSkill(subject: Subject, band: AgeBand): string {
  const defaults: Record<Subject, Record<AgeBand, string>> = {
    math: {
      "4.0-4.5": "math.shapes.basic",
      "4.5-5.0": "math.count.objects.to10",
      "5.0-5.5": "math.count.to20",
      "5.5-6.0": "math.number_bonds.to5",
      "6.0-6.5": "math.add.within10.fluency",
      "6.5-7.0": "math.add.within20.make10",
      "7.0-7.5": "math.add.within20.make10",
      "7.0+": "math.add.within20.make10",
      "7.5+": "math.add.within20.make10",
      "8.0-8.5": "math.add.within20.make10",
    },
    reading: {
      "4.0-4.5": "reading.phonological.rhyme",
      "4.5-5.0": "reading.phonological.syllable_count",
      "5.0-5.5": "reading.letter.lowercase.recognition",
      "5.5-6.0": "reading.sound.initial",
      "6.0-6.5": "reading.phonemic.blend.cvc_oral",
      "6.5-7.0": "reading.cvc.all_short_vowels.decode",
      "7.0-7.5": "reading.blends.ccvc_cvcc",
      "7.0+": "reading.blends.ccvc_cvcc",
      "7.5+": "reading.blends.ccvc_cvcc",
      "8.0-8.5": "reading.blends.ccvc_cvcc",
    },
    writing: {
      "4.0-4.5": "writing.letter.formation.lowercase",
      "4.5-5.0": "writing.letter.formation.lowercase",
      "5.0-5.5": "writing.letter.formation.lowercase",
      "5.5-6.0": "writing.word.copy.cvc",
      "6.0-6.5": "writing.grammar.noun_verb",
      "6.5-7.0": "writing.grammar.adjective.basic",
      "7.0-7.5": "writing.adjectives.comparative_superlative",
      "7.0+": "writing.adjectives.comparative_superlative",
      "7.5+": "writing.adjectives.comparative_superlative",
      "8.0-8.5": "writing.adjectives.comparative_superlative",
    },
  };
  return defaults[subject][band];
}

// Map legacy module IDs to skill graph IDs
export const LEGACY_MODULE_TO_SKILL: Record<string, string> = {
  // Pre-arithmetic (age 4)
  "math.00a": "math.count.objects.to5",
  "math.00b": "math.count.objects.to10",
  "math.00c": "math.recognize.numerals.0_to_5",
  "math.00d": "math.recognize.numerals.0_to_10",
  "math.00e": "math.write.numerals.0_to_5",
  "math.00f": "math.shapes.basic",
  "math.00g": "math.patterns.simple",
  // Arithmetic (age 5+)
  "math.01": "math.subitize.to5",
  "math.02": "math.count.to20",
  "math.03": "math.count.to20",
  "math.04": "math.count.to20",
  "math.05": "math.number_bonds.to5",
  "math.06": "math.number_bonds.to10",
  "math.07": "math.add.within5.visual",
  "math.08": "math.sub.within5.visual",
  "math.09": "math.add.within10.fluency",
  "math.10": "math.sub.within10.count_back",
  "math.11": "math.add.within20.make10",
  "math.12": "math.missing_addend.within20",
  "math.13": "math.place_value.tens_ones_100",
  "math.14": "math.add.2digit.no_regroup",
  "math.15": "math.word.add_take_apart",
  "reading.0a": "reading.phonological.rhyme",
  "reading.0b": "reading.phonological.syllable_count",
  "reading.01": "reading.letter.lowercase.recognition",
  "reading.02": "reading.sound.initial",
  "reading.03": "writing.letter.formation.lowercase",
  "reading.04": "reading.phonemic.blend.cvc_oral",
  "reading.05": "reading.cvc.encode",
  "reading.06": "reading.cvc.all_short_vowels.decode",
  "reading.07": "reading.cvc.encode",
  "reading.08": "reading.sight.highfreq.k1",
  "reading.09": "reading.blends.ccvc_cvcc",
  "reading.10": "writing.grammar.adjective.basic",
  "reading.11": "reading.digraphs.sh_ch_th",
  "reading.12": "reading.silent_e.cvc_e",
  "reading.14": "reading.doubled_consonants",
  "reading.15": "reading.silent_letters",
  "reading.16": "reading.vocabulary.academic_tier2",
  "reading.13": "reading.comprehension.literal",
};

export { ALL_SKILLS };
